import { DateTime, Interval } from "luxon";
import { useMemo } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  computeProximityPercent,
  generateSquareCharacters,
  getDirectionEmoji,
} from "../domain/geography";
import { Guess } from "../domain/guess";
import React from "react";
import { SettingsData } from "../hooks/useSettings";
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';

ReactGA.initialize('UA-109671963-1'); 
ReactGA4.initialize('G-T7BE8SGHQY'); 


const START_DATE = DateTime.fromISO("2024-01-23");

interface ShareProps {
  guesses: Guess[];
  dayString: string;
  settingsData: SettingsData;
  hideImageMode: boolean;
  rotationMode: boolean;
}

export function Share({
  guesses,
  dayString,
  settingsData,
  hideImageMode,
  rotationMode,
}: ShareProps) {
  const { t } = useTranslation();
  const { theme } = settingsData;

  const shareText = useMemo(() => {
    const win = guesses[guesses.length - 1]?.distance === 0;
    const bestDistance = Math.min(...guesses.map(({ distance }) => distance));
    const guessCount = win ? guesses.length : "X";
    const dayCount = Math.floor(
      Interval.fromDateTimes(START_DATE, DateTime.fromISO(dayString)).length(
        "day"
      )
    ) + 1;
    const difficultyModifierEmoji = hideImageMode
      ? " 🙈"
      : rotationMode
      ? " 🌀"
      : "";
    const bestPercent = `(${computeProximityPercent(
      bestDistance
    ).toString()}%)`;
    const title = `#EscutsÀPunt #${dayCount} ${guessCount}/6 ${bestPercent}${difficultyModifierEmoji}`;

    const guessString = guesses
      .map((guess) => {
        const percent = computeProximityPercent(guess.distance);
        const squares = generateSquareCharacters(percent, theme).join("");
        const direction = getDirectionEmoji(guess);
        return `${squares}${direction}`;
      })
      .join("\n");

    return [title, guessString, "escuts.apuntmedia.es"].join("\n");
  }, [dayString, guesses, hideImageMode, rotationMode, theme]);

  return (
      <button className="rounded font-bold border-2 p-1 uppercase bg-green-600 hover:bg-green-500 active:bg-green-700 text-white w-full"
        onClick={() => {
      ReactGA.event({
        category: 'Clic',
        action: 'share'
      });
      ReactGA4.event({
        category: 'Clic',
        action: 'share'
      });

          if (navigator.canShare && navigator.canShare({ text: shareText }) && navigator.userAgent.indexOf("Firefox") == -1) {
            navigator.share({
              text: shareText
            })
            .then(() => console.log('Share was successful.'))
            .catch((error) => console.log('Sharing failed: ' + error));
          } else {
            console.log(`Your system doesn't support sharing.`);
            navigator.clipboard.writeText(shareText);
            toast(t("copy"));
          }
      }}>        
        {t("share")}
      </button>
  );
}
